import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Typography from '@/components/ui/typography';
import { AlertTriangle } from 'lucide-react';
import { AppDispatch, RootState } from '@/store/store';
import {
  deleteInvitation,
  resetDeleteInvitationError,
  resetDeleteInvitationSuccess,
} from '@/store/invitationsSlice';
import { useToast } from '@/components/ui/toast';

interface DeleteInvitationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail: string;
  userUuid: string;
  tenantId: string;
}

const DeleteInvitationModal: React.FC<DeleteInvitationModalProps> = ({
  open,
  onOpenChange,
  userEmail,
  userUuid,
  tenantId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    deleteInvitationLoading,
    deleteInvitationError,
    deleteInvitationSuccess,
  } = useSelector((state: RootState) => state.invitations);
  const { addToast } = useToast();
  const [hasInitiatedDelete, setHasInitiatedDelete] = useState(false);

  // Show success toast when delete is successful
  useEffect(() => {
    if (
      hasInitiatedDelete &&
      !deleteInvitationLoading &&
      !deleteInvitationError &&
      deleteInvitationSuccess
    ) {
      addToast({
        message: deleteInvitationSuccess,
        type: 'success',
        duration: 3000,
      });
      onOpenChange(false);
      setHasInitiatedDelete(false);
      dispatch(resetDeleteInvitationSuccess());
    }
  }, [
    hasInitiatedDelete,
    deleteInvitationLoading,
    deleteInvitationError,
    deleteInvitationSuccess,
    onOpenChange,
    addToast,
    dispatch,
  ]);

  // Show error toast when delete fails
  useEffect(() => {
    if (
      hasInitiatedDelete &&
      !deleteInvitationLoading &&
      deleteInvitationError
    ) {
      addToast({
        message: deleteInvitationError,
        type: 'error',
        duration: 5000,
      });
      setHasInitiatedDelete(false);
    }
  }, [
    hasInitiatedDelete,
    deleteInvitationLoading,
    deleteInvitationError,
    addToast,
  ]);

  const handleConfirm = () => {
    dispatch(deleteInvitation({ uuid: userUuid, tenantId }));
    setHasInitiatedDelete(true);
  };

  const handleCancel = () => {
    onOpenChange(false);
    dispatch(resetDeleteInvitationError());
    addToast({
      message: 'Delete cancelled',
      type: 'info',
      duration: 2000,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-left">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <Typography
                  variant="h2"
                  weight="semibold"
                  className="text-neutral-900"
                >
                  Delete invitation
                </Typography>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <Typography variant="small" className="text-neutral-600">
            Are you sure you want to delete the invitation for{' '}
            <span className="font-medium text-neutral-900">{userEmail}</span>?
            This action cannot be undone.
          </Typography>
        </div>

        <DialogFooter className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex-1 sm:flex-none"
            disabled={deleteInvitationLoading}
          >
            Cancel
          </Button>

          <Button
            variant="destructive"
            onClick={handleConfirm}
            className="flex-1 sm:flex-none"
            disabled={deleteInvitationLoading}
          >
            {deleteInvitationLoading ? 'Deleting...' : 'Delete invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteInvitationModal;
